/**
 * The Segment SDK load (with the Braze live-auth gate) and the Segment transport
 * adapter. Imported only for these side effects, as the first import of the
 * models barrel, so both run where they always have in the boot order.
 */
// Load-bearing, in the deleted model's order: useQueryKindeProperties enters the
// scribeAPI -> store cycle, so scribeAPI evaluates with it in progress, as before.
import '@/hooks/query/useQueryKindeProperties';
import '@/pages/api/scribeAPI';

import { segmentClient } from '@/lib/analytics/legacy-sender-context';
import { createSegmentDestinationLoadGuard } from '@/lib/analytics/segment-destination-load-guard';
import { getLiveAuthSignal } from '@/lib/auth/effectiveAuthProvider';
import { isTauri } from '@/tauri/utils';
import { getDesktopVersion } from '@/utils/appVersion';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { deferTelemetryInit } from '@/utils/deferredTelemetry';
import { datadogRum } from '@datadog/browser-rum';
import { registerAnalyticsAdapter } from '@heidi/analytics';
import Router from 'next/router';

// TELEMETRY-GATE: no Segment/Braze load when telemetry is suppressed.
if (isSaasTelemetryEnabled && typeof window !== 'undefined') {
  deferTelemetryInit(() => {
    // Suppress the Braze device-mode destination unless a LIVE auth session
    // exists (see getLiveAuthSignal). Braze's "Only Track Known Users"
    // gate boots the SDK whenever Segment has ANY persisted user id string —
    // including a stale `ajs_user_id` surviving logout/expiry — and with no
    // identify to attach a user, each such browser mints an anonymous Braze
    // profile (~60k/week, Jul 2026 incident, LCM-472). All other
    // destinations (Segment.io cloud events) load unchanged. The key must
    // match the destination's creation name in the Segment workspace
    // (verified 2026-07-09). Auth state is read at load time; web auth
    // transitions are full-page redirects, so it re-evaluates each time.
    const authSignal = getLiveAuthSignal();
    const hasAuthSession = authSignal !== 'none';

    // Mint-risk attribution telemetry. A persisted Segment user id on a
    // device Braze has never attached a user to (`ab.storage.userId.*`
    // absent) is exactly the state that mints an anonymous Braze profile
    // if the destination boots without an identify landing. Braze's own
    // device data cannot distinguish our desktop webviews from real
    // browsers (WebView2 parses as Chrome), so report the state from
    // inside the app, where isTauri()/desktop_version are ground truth.
    // Counts are an upper bound on actual mints (a sign-in later in the
    // session still attaches the user) — attribution and regression
    // tripwire, not absolutes.
    try {
      // Read both identity stores the way analytics-next's user().id()
      // does ([localStorage, cookie, memory] with getAndSync write-back;
      // per-store JSON-`null` artifacts fall through like
      // UniversalStorage.get). Deliberately skips its last resort, the
      // AJS-classic legacy `ajs_user` cookie — ~zero population today,
      // accepted undercount.
      const normalizeAjsValue = (value: string | null | undefined): string | null =>
        value && value !== 'null' ? value : null;
      const idFromLocalStorage = normalizeAjsValue(
        window.localStorage.getItem('ajs_user_id')
      );
      const idFromCookie = normalizeAjsValue(
        /(?:^|;\s*)ajs_user_id=([^;]+)/.exec(document.cookie)?.[1]
      );
      const persistedAjsUserId = idFromLocalStorage ?? idFromCookie;
      const brazeKnowsUser = Object.keys(window.localStorage).some((key) =>
        key.startsWith('ab.storage.userId')
      );
      if (persistedAjsUserId && !brazeKnowsUser) {
        datadogRum.addAction('braze_boot_unidentified_device', {
          platform: isTauri() ? 'desktop' : 'web',
          desktop_version: getDesktopVersion(),
          // false ⇒ Braze booted in this state and can mint.
          braze_suppressed: !hasAuthSession,
          auth_signal: authSignal,
          // Would the Segment→Braze contains("kp_") filter ever attach
          // this id? Derived booleans only — the raw id is never sent.
          ajs_user_id_contains_kp: persistedAjsUserId.includes('kp_'),
          ajs_user_id_source: idFromLocalStorage ? 'local_storage' : 'cookie',
          // Route template (e.g. /sessions/[sessionId]) — no dynamic ids.
          route: Router.router?.pathname,
        });
      }
    } catch {
      // Attribution only — never interfere with the Segment load below.
    }

    void segmentClient.load(
      {
        writeKey: process.env.NEXT_PUBLIC_SEGMENT_SCRIBE_KEY
          ? process.env.NEXT_PUBLIC_SEGMENT_SCRIBE_KEY
          : '',
        cdnURL: 'https://sys.heidihealth.com',
        // Marks failed device-mode destination loads as handled so they don't
        // surface as unhandled rejections.
        plugins: [createSegmentDestinationLoadGuard()],
      },
      {
        integrations: {
          'Segment.io': {
            apiHost: 'sys.heidihealth.com/v1',
            protocol: 'https',
          },
          ...(hasAuthSession ? {} : { 'Braze Web Mode (Actions)': false }),
        },
      }
    );
  });
}

// P2 [3/5]: Segment transport binding for the @heidi/analytics
// pipeline. Registered at this SDK-owning site per the F5 adapter contract
// (D2) — a suppressed build registers nothing (absence, not a runtime check;
// its segmentClient is a `{ disable: true }` no-op facade anyway).
// `payload.extra` carries the per-call Segment options (pageUrl override).
// TELEMETRY-GATE.
if (isSaasTelemetryEnabled && typeof window !== 'undefined') {
  registerAnalyticsAdapter({
    destination: 'segment',
    enabled: true,
    send: (payload) => {
      void segmentClient.track(
        payload.event,
        payload.props,
        payload.extra as object | undefined
      );
    },
  });
}
